import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * Visualizador de arquivos do sistema (janela flutuante, components/VisualizadorArquivos).
 * Qualquer tela abre um arquivo com abrirArquivos([{ url, nome, tipo }], indice):
 *   url: endpoint autenticado que devolve o arquivo (ex.: /dossie/download-file/123); ou blob, se já estiver em memória
 *   nome: nome do arquivo (define como ele é exibido: PDF, imagem, texto)
 *   tipo: rótulo opcional (ex.: "Devolução")
 * Com mais de um arquivo, a janela navega entre eles.
 */
const VisualizadorArquivosContext = createContext(null);

export function VisualizadorArquivosProvider({ children }) {
    const [estado, setEstado] = useState(null);

    const abrirArquivos = useCallback((arquivos, indice = 0) => {
        // url (endpoint autenticado) ou blob (arquivo já em memória, ex.: anexo de dentro de um e-mail)
        const lista = (Array.isArray(arquivos) ? arquivos : [arquivos]).filter(a => a && (a.url || a.blob));
        if (lista.length === 0) return;
        setEstado({ arquivos: lista, indice: Math.min(Math.max(indice, 0), lista.length - 1), aberturas: Date.now() });
    }, []);

    const irPara = useCallback((indice) => {
        setEstado(atual => (atual ? { ...atual, indice: Math.min(Math.max(indice, 0), atual.arquivos.length - 1) } : atual));
    }, []);

    const fechar = useCallback(() => setEstado(null), []);

    const valor = useMemo(() => ({ estado, abrirArquivos, irPara, fechar }), [estado, abrirArquivos, irPara, fechar]);

    return <VisualizadorArquivosContext.Provider value={valor}>{children}</VisualizadorArquivosContext.Provider>;
}

export function useVisualizadorArquivos() {
    const contexto = useContext(VisualizadorArquivosContext);
    if (!contexto) {
        throw new Error("useVisualizadorArquivos precisa estar dentro de VisualizadorArquivosProvider");
    }
    return contexto;
}
